import { trackMeasurement } from "@/analytics/track";
import type { ApiCaller, ApiCallerOptions } from "@/backend/natsstubs/wire/ApiCaller";
import type { Subject } from "@/backend/natsstubs/wire/context";
import {
  ApiError,
  NoSuccessError as ApiNoSuccessError,
  TimeoutError as ApiTimeoutError,
} from "@/backend/natsstubs/wire/error";
import type { NatsApiCaller } from "@/backend/natsstubs/wire/nats/NatsApiCaller";
import { DisconnectedError } from "@/error/DisconnectedError";
import { useConnectionStore } from "@/store/connection";
import { useStatisticsStore } from "@/store/statistics";

import { BackendError } from "../BackendError";
import { NoSuccessError } from "../NoSuccessError";
import { TimeoutError } from "../TimeoutError";

/**
 * The stubs throw their own error classes; callers (modals, timeoutHandling, stores) branch on
 * the ones the WAMP path throws, so they are translated here to keep the BackendSession error
 * contract independent of the transport.
 */
export class NatsApiCallerWrapper implements ApiCaller {
  constructor(private natsCaller: NatsApiCaller) {}

  async request<T>(
    subject: Subject,
    action: string,
    payload: unknown,
    options?: ApiCallerOptions<T> & { retry?: number },
  ): Promise<T> {
    const reconnects = useConnectionStore().reconnects;
    const startTime = performance.now();
    try {
      const res = await this.natsCaller.request<T>(subject, action, payload, options);
      const elapsedTime = performance.now() - startTime;
      trackMeasurement({ type: "nats_rpc_" + action, values: { elapsedTimeMs: elapsedTime } });
      useStatisticsStore().addOk("sends");
      return res;
    } catch (err) {
      useStatisticsStore().addFail("sends", err);
      if (err instanceof ApiTimeoutError) {
        if (!useConnectionStore().isConnected) {
          throw new DisconnectedError();
        }
        const retry = options?.retry ?? 0;
        if (useConnectionStore().reconnects !== reconnects && retry < 3) {
          return this.request(subject, action, payload, { ...options, retry: retry + 1 });
        }
        throw new TimeoutError({
          request: err.contexts.request,
          response: {
            ...err.contexts.response,
            connections: useConnectionStore().wsStates(),
          },
        });
      }
      if (err instanceof ApiNoSuccessError) {
        throw new NoSuccessError(err.response.message, action, {
          request: err.contexts.request,
          response: { ...err.response, connections: useConnectionStore().wsStates() },
        });
      }
      if (err instanceof ApiError) {
        throw new BackendError(err.name, err.message, {
          request: err.contexts.request,
          response: {
            ...err.contexts.response,
            connections: useConnectionStore().wsStates(),
          },
        });
      }
      throw err;
    }
  }
}
